'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { Spring } from '@/vendor/react-spring-8';

import { normalize } from '@/utils';
import Demo, { DemoRadioControl } from '@/components/Demo';
import DefaultOutputWrapper from '@/components/Demo/OutputWrapper';

const COLORS = {
  pink: `hsl(331deg 100% 55%)`,
  yellow: `hsl(50deg 100% 50%)`,
  blue: `hsl(200deg 100% 55%)`,
};

function GradientBarDemo() {
  const id = React.useId();

  return (
    <Demo
      id={id}
      initialValues={{
        gradientStyle: 'smooth',
      }}
      outputWrapper={OutputWrapper}
      controls={[
        <DemoRadioControl
          key="gradientStyle"
          id="gradientStyle"
          label="Gradient Style"
          options={['smooth', 'bars']}
        />,
      ]}
    >
      {({ gradientStyle }) => {
        return (
          <Wrapper>
            <Spring
              to={{
                progress: gradientStyle === 'smooth' ? 0 : 1,
              }}
              config={{
                stiffness: 100,
                friction: 40,
              }}
            >
              {({ progress }) => {
                const gradientPositions =
                  getGradientPositions(progress);
                const pointPositions = getPointPositions(progress);

                return (
                  <>
                    <Gradient
                      style={{
                        background: generateGradient(
                          ...gradientPositions
                        ),
                      }}
                    />
                    <PointGroups>
                      {pointPositions.map(
                        ({ color, flex, points }, groupIndex) => (
                          <Group
                            key={groupIndex}
                            data-is-smooth={
                              gradientStyle === 'smooth'
                            }
                            style={{ flex }}
                          >
                            {points.map((p, index) => (
                              <PointWrapper
                                key={index}
                                style={{
                                  '--p': p + 'px',
                                  '--bg': color,
                                }}
                              >
                                <Point />
                              </PointWrapper>
                            ))}
                          </Group>
                        )
                      )}
                    </PointGroups>
                  </>
                );
              }}
            </Spring>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

function getGradientPositions(progress) {
  return [
    0,
    normalize(progress, 0, 1, 0, 33.3),
    normalize(progress, 0, 1, 50, 33.3),
    normalize(progress, 0, 1, 50, 66.7),
    normalize(progress, 0, 1, 100, 66.7),
    100,
  ];
}

function getPointPositions(progress) {
  const GROUP_SIZE = 200 / 3;
  const HALF_SIZE = GROUP_SIZE / 2;
  return [
    {
      color: COLORS.pink,
      flex: 2,
      points: [
        17,
        normalize(progress, 0, 1, HALF_SIZE, GROUP_SIZE) + 16,
      ],
    },
    {
      color: COLORS.yellow,
      flex: 1,
      points: [normalize(progress, 0, 1, HALF_SIZE, 0)],
    },
    {
      color: COLORS.yellow,
      flex: 1,
      points: [normalize(progress, 0, 1, 0, HALF_SIZE)],
    },
    {
      color: COLORS.blue,
      flex: 2,
      points: [
        normalize(progress, 0, 1, HALF_SIZE, 0),
        GROUP_SIZE - 1,
      ],
    },
  ];
}

function generateGradient(p1, p2, p3, p4, p5, p6) {
  return `linear-gradient(
    to bottom,
    ${COLORS.pink} ${p1}%,
    ${COLORS.pink} ${p2}%,
    ${COLORS.yellow} ${p3}%,
    ${COLORS.yellow} ${p4}%,
    ${COLORS.blue} ${p5}%,
    ${COLORS.blue} ${p6}%
  )`;
}

const OutputWrapper = styled(DefaultOutputWrapper)`
  padding: 32px 42px;
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 200px;
`;

const Gradient = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 2px;
`;

const PointGroups = styled.div`
  position: absolute;
  top: 0;
  right: -40px;
  bottom: 0;
  width: 40px;
  display: flex;
  flex-direction: column;
`;

const Group = styled.div`
  flex: 1;
  position: relative;
  z-index: 1;
  overflow: hidden;

  &:first-child {
    padding-top: 16px;
    margin-top: -16px;
  }

  &:last-child {
    padding-bottom: 16px;
    margin-bottom: -16px;
  }
`;

const PointWrapper = styled.div`
  position: absolute;
  left: 2px;
  top: 0;
  display: flex;
  flex-direction: row;
  align-items: center;

  /* prettier-ignore */
  transform: translateY(
    calc(var(--p) - 50%)
  );
  transition: opacity 600ms;
  will-change: transform;

  ${Group}:first-child &:first-child {
    z-index: 2;
  }

  ${Group}[data-is-smooth="true"]:first-child &:last-child {
    opacity: 0;
  }
  /* ${Group}[data-is-smooth="true"]:nth-child(2) &:first-child {
    opacity: 0;
  } */
  ${Group}[data-is-smooth="true"]:last-child &:first-child {
    opacity: 0;
  }

  &::before {
    content: '';
    width: 8px;
    height: 2px;
    background-color: black;
    border-radius: 100px 0px 0px 100px;
  }
`;

const Point = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid black;
  background-color: var(--bg);
  box-shadow: 0px 1px 2px hsl(0deg 0% 0% / 0.2);
`;

export default GradientBarDemo;
